import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { scheduleService } from '../services/scheduleService';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import Dismissible from './common/Dismissible';
import { usePlayer } from '../player/PlayerContext';
import { formatTime } from '../player/format';
import { openPricingModal } from '../utils/pricingModal';
import '../player/player.css';

const DEFAULT_VOICE_ID = 'Joanna';
const DEFAULT_LANGUAGE_CODE = 'en-US';

// Helper function to process content with translation and SSML interlacing
const processContentWithTranslation = async (content, sourceLanguage, translationLanguage, translationVoiceId) => {

  try {
    // Split content into sentences using more sophisticated regex
    // This handles periods, exclamation marks, question marks, and common abbreviations
    const sentences = content
      .replace(/([.!?])\s*(?=[A-Z])/g, '$1|SPLIT|') // Mark sentence boundaries
      .replace(/(\w\.)(\w)/g, '$1 $2') // Handle abbreviations
      .split('|SPLIT|')
      .map(s => s.trim())
      .filter(s => s.length > 10); // Filter out very short fragments
    

    const processedSentences = [];

    for (let i = 0; i < sentences.length; i++) {
      const sentence = sentences[i].trim();
      if (!sentence) continue;


      try {
        // Translate the sentence using AWS Translate
        
        const translatedSentence = await apiService.translateText(sentence, sourceLanguage, translationLanguage);
        
        // Create SSML for source sentence with proper escaping
        const escapedSource = sentence.replace(/[<>&"']/g, (match) => {
          const escapeMap = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' };
          return escapeMap[match];
        });
        
        // Create SSML for translated sentence with proper escaping
        const escapedTranslated = translatedSentence.replace(/[<>&"']/g, (match) => {
          const escapeMap = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' };
          return escapeMap[match];
        });
        
        // Create SSML for source sentence
        const sourceSSML = `<speak><voice name="${DEFAULT_VOICE_ID}"><lang xml:lang="${sourceLanguage}">${escapedSource}</lang></voice></speak>`;
        
        // Create SSML for translated sentence  
        const translatedSSML = `<speak><voice name="${translationVoiceId}"><lang xml:lang="${translationLanguage}">${escapedTranslated}</lang></voice></speak>`;

        // Add both to processed sentences array (interlaced pattern)
        processedSentences.push(sourceSSML);
        processedSentences.push(translatedSSML);
        
        
      } catch (sentenceError) {
        console.error(`❌ Error processing sentence ${i + 1}:`, sentenceError);
        console.error(`❌ Full error details:`, {
          message: sentenceError.message,
          stack: sentenceError.stack,
          name: sentenceError.name,
          sentence: sentence.substring(0, 100) + '...',
          sourceLanguage,
          translationLanguage,
          translationVoiceId
        });
        // Fall back to original sentence without translation
        const escapedSource = sentence.replace(/[<>&"']/g, (match) => {
          const escapeMap = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' };
          return escapeMap[match];
        });
        const fallbackSSML = `<speak><voice name="${DEFAULT_VOICE_ID}"><lang xml:lang="${sourceLanguage}">${escapedSource}</lang></voice></speak>`;
        processedSentences.push(fallbackSSML);
      }
    }

    // Join all SSML sentences with line breaks for final content
    const finalContent = processedSentences.join('\n\n');
    

    return finalContent;

  } catch (error) {
    console.error('❌ Translation processing failed:', error);
    throw new Error(`Translation failed: ${error.message}`);
  }
};

function PodcastPlayer({ user }) {
  const [podcasts, setPodcasts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  // Shared audio player — the single Audio instance and all playback state
  // live in PlayerContext (mounted in App.js), so playback survives tab
  // switches. This component only consumes it.
  const { currentlyPlaying, isPlaying, currentTime, duration, notice, play, togglePause, seek } =
    usePlayer();

  // News sources management state
  const [newsSources, setNewsSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  
  // Schedule-based episodes state
  const [schedules, setSchedules] = useState([]);
  const [scheduleEpisodes, setScheduleEpisodes] = useState({});
  const [expandedSchedules, setExpandedSchedules] = useState({});
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // Load user's podcasts when component mounts
  useEffect(() => {
    const savedPodcasts = localStorage.getItem(`podcasts_${user.username}`);
    if (savedPodcasts) {
      setPodcasts(JSON.parse(savedPodcasts));
    }
    // No default/sample podcasts - users must generate their own
  }, [user.username]);

  // Function to load news sources from database
  const loadNewsSources = useCallback(async () => {
    try {
      setLoadingSources(true);
      const sources = await apiService.getUserSources(user.username);
      setNewsSources(sources);
    } catch (error) {
      console.error('❌ Error loading news sources:', error);
      setMessage(`❌ Error loading news sources: ${error.message}`);
    } finally {
      setLoadingSources(false);
    }
  }, [user?.username]);
  
  // Load schedules and their episodes
  const loadSchedulesAndEpisodes = useCallback(async () => {
    try {
      setLoadingSchedules(true);
      
      // Load all schedules
      const schedulesData = await scheduleService.listSchedules();
      const activeSchedules = schedulesData.filter(s => s.status !== 'deleted');
      setSchedules(activeSchedules);
      
      // Load episodes for each schedule
      const episodesData = {};
      for (const schedule of activeSchedules) {
        try {
          const history = await scheduleService.getExecutionHistory(schedule.scheduleId);
          
          // Filter only successful executions with episodes
          let successfulEpisodes = history.filter(h => {
            return h.status === 'success' && h.episodeId;
          });
          
          // FALLBACK: If executionHistory is empty but we have lastEpisodeId, create episodes from RSS feed
          if (successfulEpisodes.length === 0 && schedule.lastEpisodeId && schedule.lastExecutionResult === 'success') {
            
            // Try to fetch ALL episodes from RSS feed
            if (schedule.rssFeedUrl) {
              try {
                const rssResponse = await fetch(schedule.rssFeedUrl);
                const rssText = await rssResponse.text();
                
                // Parse RSS XML to find all episodes
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(rssText, 'text/xml');
                const items = xmlDoc.getElementsByTagName('item');
                
                
                // Parse all items into episodes
                const rssEpisodes = [];
                for (let i = 0; i < items.length; i++) {
                  const item = items[i];
                  
                  // Get episode ID from guid
                  const guidElement = item.getElementsByTagName('guid')[0];
                  const episodeId = guidElement?.textContent;
                  
                  if (!episodeId) {
                    console.warn(`⚠️ Skipping item ${i} - no episode ID found`);
                    continue;
                  }
                  
                  // Get title
                  const titleElement = item.getElementsByTagName('title')[0];
                  const episodeTitle = titleElement?.textContent || `Episode ${i + 1}`;
                  
                  // Get publication date
                  const pubDateElement = item.getElementsByTagName('pubDate')[0];
                  const timestamp = pubDateElement?.textContent || new Date().toISOString();
                  
                  // Get duration
                  let episodeAudioLength = null;
                  const durationElement = item.getElementsByTagName('itunes:duration')[0];
                  const durationText = durationElement?.textContent;

                  if (durationText) {
                    if (durationText.includes(':')) {
                      const parts = durationText.split(':').map(Number);
                      if (parts.length === 3) {
                        episodeAudioLength = parts[0] * 3600 + parts[1] * 60 + parts[2];
                      } else if (parts.length === 2) {
                        episodeAudioLength = parts[0] * 60 + parts[1];
                      }
                    } else {
                      episodeAudioLength = parseInt(durationText);
                    }
                  }
                  
                  // Get audio URL from enclosure tag
                  let episodeAudioUrl = null;
                  const enclosure = item.getElementsByTagName('enclosure')[0];
                  if (enclosure) {
                    episodeAudioUrl = enclosure.getAttribute('url');
                  }
                  
                  // Count articles from description (approximate)
                  const descriptionElement = item.getElementsByTagName('description')[0];
                  const description = descriptionElement?.textContent || '';
                  const articleMatches = description.match(/\d+\./g);
                  const articlesCount = articleMatches ? articleMatches.length : (schedule.newsSources?.length || 1);
                  
                  rssEpisodes.push({
                    episodeId: episodeId,
                    episodeTitle: episodeTitle,
                    timestamp: new Date(timestamp).toISOString(),
                    status: 'success',
                    articlesCount: articlesCount,
                    audioLength: episodeAudioLength,
                    duration: episodeAudioLength,
                    audioUrl: episodeAudioUrl
                  });
                  
                }
                
                successfulEpisodes = rssEpisodes;
              } catch (rssError) {
                console.error(`❌ Could not parse RSS feed:`, rssError);
                
                // Last resort fallback: create single episode from schedule data
                const fallbackEpisode = {
                  episodeId: schedule.lastEpisodeId,
                  episodeTitle: `${schedule.scheduleName} - ${formatDate(schedule.lastRunTime)}`,
                  timestamp: schedule.lastRunTime,
                  status: 'success',
                  articlesCount: schedule.newsSources?.length || 1,
                  audioLength: null,
                  duration: null,
                  audioUrl: null
                };
                successfulEpisodes = [fallbackEpisode];
              }
            } else {
              console.warn(`⚠️ No RSS feed URL available for schedule ${schedule.scheduleName}`);
            }
          }
          
          episodesData[schedule.scheduleId] = successfulEpisodes;
        } catch (error) {
          console.error(`❌ Failed to load episodes for schedule ${schedule.scheduleId}:`, error);
          episodesData[schedule.scheduleId] = [];
        }
      }
      
      setScheduleEpisodes(episodesData);
    } catch (error) {
      console.error('❌ Error loading schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  // Load user's news sources from database when component mounts
  useEffect(() => {
    loadNewsSources();
    loadSchedulesAndEpisodes();
  }, [loadNewsSources, loadSchedulesAndEpisodes]);

  const generateRSSFeed = () => {
    // RSS feeds are now per-schedule, not per-user
    // Direct users to the Schedule Management tab instead
    setMessage('📡 RSS feeds are available in the Schedule Management tab. Each schedule has its own RSS feed URL that you can copy and add to your podcast app.');
    setTimeout(() => setMessage(''), 5000);
  };

  const handlePlayPodcast = async (podcast) => {
    // Playback itself is handled by the shared PlayerContext; this wrapper
    // only adds this tab's status messages.
    if (currentlyPlaying?.id === podcast.id && isPlaying) {
      togglePause();
      setMessage('⏸️ Podcast paused');
    } else {
      try {
        await play(podcast);
        setMessage(`🎙️ Now playing: ${podcast.title}`);
      } catch (error) {
        console.error('Error playing podcast:', error);
        setMessage('❌ Error playing podcast. Please check your browser settings.');
      }
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDownload = (podcast) => {
    try {
      
      if (!podcast.audioUrl) {
        throw new Error('No audio file available for download. All podcasts must have AWS Polly audio files.');
      }
      
      // Download AWS Polly generated audio file
      setMessage(`📥 Downloading AWS Polly audio file: ${podcast.title}...`);
      apiService.downloadAudio(podcast);
      
      setTimeout(() => {
        setMessage(`✅ Download started! Check your downloads folder for ${podcast.title}.mp3`);
      }, 1000);
      
    } catch (error) {
      console.error('Download error:', error);
      setMessage('❌ Error downloading file. Please try again.');
    }
    
    setTimeout(() => setMessage(''), 7000);
  };


  const handleGenerateNew = async () => {
    setGenerating(true);
    setProgress(0);
    setProgressText('Initializing...');
    setMessage('🔄 Starting podcast generation...');
    const languageCode = DEFAULT_LANGUAGE_CODE;

    try {
      setProgress(10);
      setProgressText('Checking news sources...');

      // Use the already loaded news sources
      const sources = newsSources;
      
      if (sources.length === 0) {
        setMessage('❌ No news sources found. Please add sources from the News Sources tab before generating.');
        setGenerating(false);
        setProgress(0);
        setProgressText('');
        return;
      }


      setProgress(20);
      setProgressText('Processing news sources...');

      // Process each news source and combine content
      let combinedContent = '';
      let combinedTitle = 'Daily News Digest';
      const processedSources = [];
      
      // Collect language attributes from sources for the digest
      const sourceLanguages = new Set();
      const translationLanguages = new Set();
      const translationVoiceIds = new Set();
      
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        
        // Safe debugging without JSON.stringify
        
        
        // Collect language attributes for digest metadata FIRST
        sourceLanguages.add(source.sourceLanguage || source.language || 'en');
        
        
        if (source.translationLanguage) {
          translationLanguages.add(source.translationLanguage);
        } else {
        }
        
        if (source.translationVoiceId) {
          translationVoiceIds.add(source.translationVoiceId);
        } else {
        }
        
        setProgressText(`📰 Extracting content from ${source.name || `source ${i + 1}`}...`);
        setProgress(20 + (i * 25 / sources.length)); // Allocate 25% for content extraction
        
        try {
          // Extract news content from each source URL
          const newsData = await apiService.extractNewsContent(source.url, languageCode);
          
          let processedContent = newsData.content;
          
          // Check if translation is required for this source
          
          if (source.translationLanguage && source.translationVoiceId) {
            
            setProgressText(`🌐 Translating and creating SSML for ${source.name}...`);
            setProgress(45 + (i * 25 / sources.length)); // Additional 25% for translation
            
            // Process content with translation and SSML interlacing
            processedContent = await processContentWithTranslation(
              newsData.content,
              source.sourceLanguage || source.language || 'en',
              source.translationLanguage,
              source.translationVoiceId
            );
            
          } else {
          }
          
          // Log what's being added to combined content
          const contentToAdd = `\n\nFrom ${newsData.title || source.name}:\n\n${processedContent}\n`;
          
          combinedContent += contentToAdd;
          processedSources.push({
            name: source.name,
            url: source.url,
            title: newsData.title,
            wordCount: newsData.word_count,
            sourceLanguage: source.sourceLanguage || source.language || 'en',
            translationLanguage: source.translationLanguage || null,
            translationVoiceId: source.translationVoiceId || null,
            hasTranslation: !!(source.translationLanguage && source.translationVoiceId)
          });
          
        } catch (sourceError) {
          console.warn(`⚠️ Failed to process source ${source.name}:`, sourceError.message);
          // Continue with other sources
        }
      }
      
      if (!combinedContent.trim()) {
        throw new Error('No content could be extracted from any news sources');
      }

      setProgress(70);
      setProgressText('💾 Saving news digest...');
      
      // Log the final combined content for debugging
      
      // Check if content contains SSML tags
      const hasSSMLTags = combinedContent.includes('<speak>') || combinedContent.includes('<voice>') || combinedContent.includes('<lang>');
      
      // Calculate word count excluding SSML tags for better accuracy
      const contentForWordCount = combinedContent.replace(/<[^>]*>/g, ' ').trim();
      const actualWordCount = contentForWordCount.split(/\s+/).filter(word => word.length > 0).length;
      
      
      // Create a comprehensive news digest
      const newsDigest = {
        title: combinedTitle,
        content: combinedContent.trim(),
        url: `digest-${Date.now()}`,
        summary: combinedContent.replace(/<[^>]*>/g, ' ').trim().substring(0, 300) + '...',
        authors: ['News Digest Team'],
        publish_date: new Date().toISOString(),
        word_count: actualWordCount,
        sources: processedSources,
        hasTranslation: hasSSMLTags
      };

      // Prepare source options for saving with language attributes
      
      // Check if any sources required translation
      const hasTranslatedSources = processedSources.some(source => source.hasTranslation);
      
      const sourceOptions = {
        sourceLanguage: sourceLanguages.size === 1 ? Array.from(sourceLanguages)[0] : 'mixed',
        translationLanguage: translationLanguages.size === 1 ? Array.from(translationLanguages)[0] : 
                           translationLanguages.size > 1 ? 'mixed' : null,
        translationVoiceId: translationVoiceIds.size === 1 ? Array.from(translationVoiceIds)[0] : 
                          translationVoiceIds.size > 1 ? 'mixed' : null
      };
      

      // Save the digest to database
      const saveResult = await apiService.saveNewsArticle(newsDigest, user.username, sourceOptions);
      
      setProgress(85);
      setProgressText('🎵 Generating audio with selected voice...');
      
      // Generate audio from the combined content
      const audioResult = await apiService.generateAudioFromText(combinedContent, {
        voice: DEFAULT_VOICE_ID,
        language: languageCode,
        articleId: saveResult.articleId,
        userId: user.username
      });
      
      
      // Validate that we have a proper AWS Polly audio URL
      if (!audioResult.audioUrl) {
        throw new Error('AWS Polly failed to generate audio file. No audio URL returned.');
      }
      
      
      // Check for combined audio message
      if (audioResult.combinedAudio) {
        setMessage(`✅ Large content: Audio combined from ${audioResult.totalChunks} parts into single file for seamless playback`);
      } else if (audioResult.totalChunks > 1) {
        setMessage(`⚠️ Large content: Audio combination failed, using first part only`);
      }
      
      setProgress(95);
      setProgressText('✅ Finalizing podcast...');
      
      // Create the final podcast object with AWS audio
      const newPodcast = {
        id: saveResult.articleId,
        title: combinedTitle,
        duration: audioResult.duration ? Math.round(audioResult.duration) + 's' : 'Generated',
        date: new Date().toISOString().split('T')[0],
        sources: processedSources.map(s => s.name),
        audioUrl: audioResult.audioUrl, // Guaranteed to exist now
        audioResult: audioResult,
        content: combinedContent,
        wordCount: newsDigest.word_count,
        status: 'ready',
        type: 'aws-polly-generated'
      };
      
      
      // Add to podcasts list at the beginning
      const updatedPodcasts = [newPodcast, ...podcasts];
      setPodcasts(updatedPodcasts);
      
      // Save to localStorage
      localStorage.setItem(`podcasts_${user.username}`, JSON.stringify(updatedPodcasts));
      
      setProgress(100);
      setProgressText('🎉 AWS Polly podcast ready!');
      setMessage('✅ Podcast generated successfully with AWS Polly! Click the play button to listen or download the audio file.');
      
      // Clear progress after 3 seconds, message after 10 seconds
      setTimeout(() => {
        setProgress(0);
        setProgressText('');
      }, 3000);
      setTimeout(() => setMessage(''), 10000);
        
    } catch (error) {
      console.error('❌ Podcast generation failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      if (error.waitlisted) {
        // Structured 403 (§9, free-tier-cap-enforcement plan): this is the conversion moment,
        // not a broken app — open the pricing modal with waitlist copy instead of an error toast.
        setMessage(`⏳ ${error.waitlistMessage || "You're on the free-tier waitlist — upgrade to Plus to skip the line."}`);
        setProgressText('');
        setProgress(0);
        openPricingModal();
      } else {
        setMessage(`❌ Generation failed: ${error.message}`);
        setProgressText('❌ Generation failed');
        setProgress(0);
      }
    } finally {
      setGenerating(false);
      setTimeout(() => {
        setMessage('');
        setProgress(0);
        setProgressText('');
      }, 5000);
    }
  };

  return (
    <div className="component-card">
      <h2>🎙️ My Podcasts</h2>

      {(message || notice) && (
        <div className={(message || notice).includes('✅') || (message || notice).includes('📋') ? 'success' : (message || notice).includes('❌') ? 'error' : 'loading'}>
          {message || notice}
        </div>
      )}

      {/* The sticky player bar is rendered globally in App.js (PlayerBar). */}

      {/* Podcasts List - Schedule Based */}
      <div>
        <h3>Generated Podcasts by Schedule</h3>
        
        {loadingSchedules ? (
          <div className="loading-state">
            <p>⏳ Loading schedules and episodes...</p>
          </div>
        ) : schedules.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon" aria-hidden="true">📅</div>
            <h3 className="empty-state__title">No schedules found</h3>
            <p className="empty-state__text">Create a schedule in the "Schedule Management" tab to automatically generate podcasts!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {schedules.map((schedule) => {
              const episodes = (scheduleEpisodes[schedule.scheduleId] || [])
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Sort by timestamp, latest first
              const isExpanded = expandedSchedules[schedule.scheduleId];
              
              return (
                <div 
                  key={schedule.scheduleId}
                  style={{ 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    background: 'white'
                  }}
                >
                  {/* Schedule Header - Clickable */}
                  <div
                    onClick={() => setExpandedSchedules(prev => ({
                      ...prev,
                      [schedule.scheduleId]: !prev[schedule.scheduleId]
                    }))}
                    style={{ 
                      padding: '1.5rem',
                      background: 'linear-gradient(135deg, var(--color-sky) 0%, var(--color-primary) 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>
                        📅 {schedule.scheduleName}
                      </h4>
                      <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                        {episodes.length} episode{episodes.length !== 1 ? 's' : ''} • 
                        {schedule.newsSources && schedule.newsSources.length > 0 && 
                          ` Source: ${schedule.newsSources[0].name}`
                        }
                      </div>
                    </div>
                    <div style={{ fontSize: '1.5rem', transition: 'transform 0.3s ease', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </div>
                  </div>
                  
                  {/* Episodes List - Expandable */}
                  {isExpanded && (
                    <div style={{ padding: '1rem' }}>
                      {episodes.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                          <p>🎙️ No episodes generated yet for this schedule.</p>
                          <p style={{ fontSize: '0.9rem' }}>Episodes will appear here automatically when generated.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {episodes.map((episode, idx) => {
                            const episodeData = {
                              id: episode.episodeId,
                              title: episode.episodeTitle || `Episode ${idx + 1}`,
                              duration: episode.audioLength ? `${Math.floor(episode.audioLength / 60)}:${(episode.audioLength % 60).toString().padStart(2, '0')}` : 'N/A',
                              date: episode.timestamp,
                              sources: schedule.newsSources?.map(s => s.name) || [],
                              audioUrl: episode.audioUrl
                            };
                            
                            return (
                              <div 
                                key={episode.episodeId}
                                className="episode-card"
                                style={{ 
                                  border: '1px solid #e2e8f0', 
                                  borderRadius: '8px', 
                                  padding: 'clamp(0.75rem, 2vw, 1.25rem)',
                                  background: '#fafafa',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                              >
                                <div className="episode-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '1rem' }}>
                                  <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                                    <h5 style={{ margin: '0 0 0.5rem 0', color: '#2d3748', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)', wordBreak: 'break-word' }}>
                                      {episodeData.title}
                                    </h5>
                                    <div className="episode-meta" style={{ display: 'flex', gap: '1rem', fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', color: '#718096', flexWrap: 'wrap' }}>
                                      <span>⏱️ {episodeData.duration}</span>
                                      <span>📅 {formatDateTime(episode.timestamp)}</span>
                                      {episode.articlesCount && <span>📰 {episode.articlesCount} articles</span>}
                                      {(episode.episodeFormat || 'article') === 'conversational' && (
                                        <span>
                                          🎙️ Two-host conversation
                                          {['easy', 'medium', 'hard'].includes(episode.conversationalDifficulty) &&
                                            ` · ${episode.conversationalDifficulty.charAt(0).toUpperCase()}${episode.conversationalDifficulty.slice(1)}`}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="episode-actions" style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center' }}>
                                    {episodeData.audioUrl ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => handlePlayPodcast(episodeData)}
                                          className={`btn btn-primary episode-play-btn${currentlyPlaying?.id === episodeData.id && isPlaying ? ' is-playing' : ''}`}
                                          aria-label={currentlyPlaying?.id === episodeData.id && isPlaying ? `Pause ${episodeData.title}` : `Play ${episodeData.title}`}
                                        >
                                          {currentlyPlaying?.id === episodeData.id && isPlaying ? '⏸️ Pause' : '▶️ Play'}
                                        </button>
                                        <button
                                          onClick={() => handleDownload(episodeData)}
                                          className="btn btn-secondary"
                                          style={{ 
                                            fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', 
                                            padding: 'clamp(0.4rem, 1.5vw, 0.5rem) clamp(0.75rem, 2vw, 1rem)',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          📥 Download
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        disabled
                                        className="btn btn-secondary"
                                        style={{ 
                                          fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', 
                                          padding: 'clamp(0.4rem, 1.5vw, 0.5rem) clamp(0.75rem, 2vw, 1rem)',
                                          opacity: 0.5,
                                          cursor: 'not-allowed'
                                        }}
                                        title="Audio file not available"
                                      >
                                        ⏳ Processing...
                                      </button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Audio progress bar */}
                                {currentlyPlaying?.id === episodeData.id && isPlaying && (
                                  <div className="episode-nowplaying">
                                    <div className="episode-nowplaying__row">
                                      <span>🎵 Now Playing</span>
                                      <span>{formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : episodeData.duration}</span>
                                    </div>
                                    <div
                                      className="player-scrubber"
                                      role="slider"
                                      tabIndex={0}
                                      aria-label="Seek"
                                      aria-valuemin={0}
                                      aria-valuemax={duration > 0 ? Math.round(duration) : 0}
                                      aria-valuenow={Math.round(currentTime)}
                                      aria-valuetext={`${formatTime(currentTime)} of ${duration > 0 ? formatTime(duration) : episodeData.duration}`}
                                      onClick={(e) => {
                                        if (duration > 0) {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          seek((e.clientX - rect.left) / rect.width);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (duration <= 0) return;
                                        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                                          e.preventDefault();
                                          seek((currentTime + 5) / duration);
                                        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                                          e.preventDefault();
                                          seek((currentTime - 5) / duration);
                                        } else if (e.key === 'Home') {
                                          e.preventDefault();
                                          seek(0);
                                        } else if (e.key === 'End') {
                                          e.preventDefault();
                                          seek(1);
                                        }
                                      }}
                                    >
                                      {/* width is playback-position-driven — must stay a dynamic style */}
                                      <div
                                        className="player-scrubber__fill"
                                        style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Instructions */}
      <Dismissible id="podcasts-how" title="How It Works" userSub={user?.username}>
        <div style={{ marginTop: '2rem', padding: '1rem', background: '#fffbeb', borderRadius: '8px', border: '1px solid #F9C500' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e' }}>💡 How It Works</h4>
          <ol style={{ margin: '0', paddingLeft: '1.5rem', color: '#92400e', fontSize: '0.9rem' }}>
            <li>Add news sources in the "News Sources" tab</li>
            <li>Create a schedule in the "Schedules" tab — episodes generate automatically at the times you set</li>
            <li>AI converts article text to natural speech using AWS Polly</li>
            <li>Listen here, download the audio, or subscribe via RSS feed</li>
          </ol>
        </div>
      </Dismissible>
    </div>
  );
}

export default PodcastPlayer;
